// Header product search: the only part loaded on every page. It wires the header trigger and the
// shortcuts (Ctrl/⌘ K anywhere, "/" outside text fields) and loads the palette (productSearch.ts)
// plus its stylesheet on first intent: hover or focus on the trigger warms them, a click opens.
// Nothing here touches the page's layout or runs on scroll.

import { isTypingTarget, shortcutLabel } from "./searchModel";

type Palette = { open(opener?: HTMLElement | null): void };

const DEFAULT_CSS = "/frontend/assets/css/product-search.css";
let loading: Promise<Palette> | null = null;

function trigger(): HTMLElement | null {
    return document.querySelector<HTMLElement>("[data-psearch-open]");
}

function loadCss(): Promise<void> {
    if (document.querySelector("link[data-psearch-style]")) return Promise.resolve();
    const href = trigger()?.dataset.psearchCss || DEFAULT_CSS;
    return new Promise(resolve => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.setAttribute("data-psearch-style", "");
        link.onload = () => resolve();
        link.onerror = () => resolve(); // the palette still works unstyled rather than not at all
        document.head.append(link);
    });
}

function warm(): Promise<Palette> {
    if (!loading) {
        loading = Promise.all([loadCss(), import("./productSearch")]).then(([, m]) => m.createPalette());
        loading.catch(() => { loading = null; });
    }
    return loading;
}

function open(from: HTMLElement | null): void {
    warm().then(p => p.open(from)).catch(() => {
        // offline or a failed chunk: the full catalogue search still works
        const lang = (document.documentElement.lang || "").slice(0, 2).toLowerCase();
        window.location.assign((lang === "ar" || lang === "fr" ? "/" + lang : "") + "/products");
    });
}

function init(): void {
    const btn = trigger();
    if (btn) {
        // No visible shortcut chip (touch screens have no Ctrl key); assistive tech still hears it.
        const platform = (navigator as any).userAgentData?.platform || navigator.platform || "";
        btn.setAttribute("aria-keyshortcuts", shortcutLabel(platform).startsWith("⌘") ? "Meta+K" : "Control+K");
        btn.addEventListener("click", e => { e.preventDefault(); open(btn); });
        btn.addEventListener("pointerenter", () => { void warm(); }, { once: true });
        btn.addEventListener("focus", () => { void warm(); }, { once: true });
        btn.addEventListener("touchstart", () => { void warm(); }, { once: true, passive: true });
    }

    document.addEventListener("keydown", e => {
        if (e.defaultPrevented || e.isComposing) return;
        const k = e.key.toLowerCase();
        if (k === "k" && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
            e.preventDefault();
            open(btn);
        } else if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey
            && !isTypingTarget(document.activeElement as HTMLElement | null)
            && !document.querySelector("dialog[open]")) {
            e.preventDefault();
            open(btn);
        }
    });
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
else init();
